import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { identity } from '../data/profile'
import { PERSON_ID } from '../lib/structuredData'

const SITE_URL = identity.website
const SITE_NAME = identity.name

// Absolute canonical URL for a path: no query, no hash, no trailing slash (except "/").
function canonicalUrl(path) {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, '')
  return `${SITE_URL}${clean || '/'}`
}

/**
 * Page metadata. Every page renders one <SEO>.
 * - title: the full title (<= 60 characters). " | Martin Tamm" is added only
 *   when the title does not already name Martin Tamm and still fits.
 * - description: <= 160 characters, unique per page.
 * - path: canonical path; defaults to the current route.
 * - noindex: for pages that must not be indexed (404); no canonical then.
 * - image: absolute path under public/ for link previews (optional).
 * Also emits a WebPage JSON-LD block, so every page carries valid structured data.
 */
export default function SEO({ title, description, path, noindex = false, image, type = 'website' }) {
  const location = useLocation()
  const suffixed = `${title} | ${SITE_NAME}`
  const fullTitle = title.includes(SITE_NAME) || suffixed.length > 60 ? title : suffixed
  const url = canonicalUrl(path ?? location.pathname)
  const imageUrl = image ? `${SITE_URL}${image}` : null

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: fullTitle,
    description,
    url,
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
    about: { '@id': PERSON_ID },
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex" /> : <link rel="canonical" href={url} />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta name="twitter:card" content={imageUrl ? 'summary_large_image' : 'summary'} />

      {!noindex && <script type="application/ld+json">{JSON.stringify(webPage)}</script>}
    </Helmet>
  )
}
